import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { setActiveItem } from "units/dashboard/store/menuSlice";
import ClientSidebar from "../component/ClientSidebar";
import { useClientConversations } from "../hooks/useClientConversations";
import ClientBotHeader from "./ClientBotHeader";
import { toast } from "react-toastify";
import { showToast } from "toast/toastHelper";
import { getBotStatus } from "units/bot/services/botService";
import Footer from "units/dashboard/components/Footer";
import ClientContentRouter from "./ClientContentRouter";
import { clientInfo } from "../services/ClientProfileService";
import MobileSidebarToggle from "./components/MobileSidebarToggle";
import { createChatbotRoom, deleteChatbotRoom } from "../services/clientChatBotService";
import { ClientSidebarProvider as SidebarProvider } from "units/client/context/ClientSidebarProvider";

const ClientChatBotContainer = ({ headerTitle }) => {
  const [chatHeight, setChatHeight] = useState("calc(100vh - 200px)");
  const [isChatBotVisible, setIsChatBotVisible] = useState(true);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isBotActive, setIsBotActive] = useState(true);
  const [welcomeMessage, setWelcomeMessage] = useState("");

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { username } = useParams();

  const user = useSelector((state) => state.userInfo);
  const userId = user?.userId;
  const accessToken = useSelector((state) => state.auth.accessToken);
  const selectedMenu = useSelector((state) => state.menu.activeItem);
  const menuVersion = useSelector((state) => state.menu.version);
  const name = useSelector((state) => state?.botInfo?.name);
  const [isRoomLoading, setIsRoomLoading] = useState(false);
  const { conversations, error, loadConversations, loading } =
    useClientConversations({ username, userId });
  const [isCustomerPaymentActive, setIsCustomerPaymentActive] = useState(false);
  const isImageRoute = location.pathname.startsWith("/bot/image");
  const isWalletRoute = location.pathname.startsWith("/bot/wallet");
  const isPaymentMethod = location.pathname.startsWith("/bot/payment");
  const isEdit = location.pathname.startsWith("/bot/edit");
  const isPaymentFeedback = location.pathname.startsWith("/bot/client-payment-feedback");
  const isChangePassword = location.pathname.startsWith("/bot/client-change-password");
  const isTransactionList = location.pathname.startsWith("/bot/client-transaction-list");

  useEffect(() => {
    const checkReminderMessage = async () => {
      try {
        const res = await clientInfo({ accessToken });
        if (res?.data?.reminderMessage) {
          showToast(res.data.reminderMessage, "info");
        }
      } catch (error) {
        console.log(error);
      }
    };

    if (accessToken) {
      checkReminderMessage();
    }
  }, [accessToken]);

  useEffect(() => {
    const init = async () => {
      try {
        const res = await getBotStatus(username);
        setIsBotActive(res?.botStatus?.status);
        if (res?.customerPaymentEnabled) {
          setIsCustomerPaymentActive(true);
        }
        if (res?.welcomeMessage) {
          setWelcomeMessage(res?.welcomeMessage);
        }
      } catch (err) {
        console.error("getBotStatus failed:", err);
        setIsBotActive(false);
      }
    };

    if (username) {
      init();
    }
  }, [username]);

  useEffect(() => {
    if (!accessToken) return;

    const fetchConversations = async () => {
      setIsLoading(true);

      try {
        await loadConversations();
      } finally {
        setIsLoading(false);
      }
    };

    fetchConversations();
  }, [selectedMenu, menuVersion, accessToken, isBotActive]);

  useEffect(() => {
    if (isBotActive === false) {
      showToast("درحال بروزرسانی ، بزودی در دسترس قرار میگیرد", "info");
    }
  }, [isBotActive]);

  const handleConversationClick = (conversation) => {
    if (!conversation) return;

    dispatch(setActiveItem("chatbox"));
    setSelectedConversation(conversation);
    setIsChatBotVisible(false);

    if (!location.pathname.includes(`/bot/chatbox/${username}`)) {
      navigate(`/bot/chatbox/${username}`);
    }
  };

  const handleNewChat = async () => {
    try {
      dispatch(setActiveItem("chatbox"));
      navigate(`/bot/chatbox/${username}`);

      setSelectedConversation(null);
      setIsChatBotVisible(false);
      setIsRoomLoading(true);
      const newChatroom = await createChatbotRoom({
        accessToken,
        username
      });

      if (newChatroom?.ok && newChatroom?.data?.id) {
        setSelectedConversation(newChatroom.data);
        setWelcomeMessage(newChatroom.data.welcomeMessage || "");
        return;
      }

      console.error("خطا در ایجاد چت جدید:", newChatroom);
    } catch (error) {
      console.error("خطا در ایجاد چت جدید:", error);

      if (error?.overLimit) {
        toast.error(error.message);
        setTimeout(() => navigate(`/${username}`), 1500);
      } else if (error?.statusCode === 403) {
        navigate(`/${username}`);
      } else {
        toast.error(error?.message || "خطای ناشناخته");
      }
    } finally {
      setIsRoomLoading(false);
    }
  };

  const toggleView = async () => {
    if (isChatBotVisible) {
      await handleNewChat();
      return;
    }

    setIsChatBotVisible(true);
    setSelectedConversation(null);
    await loadConversations();
  };

  const toggleChatBotVisibility = () => {
    setIsChatBotVisible(true);
    setSelectedConversation(null);
  };

  useEffect(() => {
    const elements = [
      document.getElementById("chat-input"), // فقط این یکی واقعاً وجود داره الان
    ];

    const calculate = () => {
      const total = elements.reduce(
        (sum, el) => sum + (el?.offsetHeight || 0),
        0
      );

      setChatHeight(`calc(100vh - ${total + 20}px)`);
    };

    calculate();

    const observer = new ResizeObserver(calculate);

    elements.forEach((el) => {
      if (el) observer.observe(el);
    });

    window.addEventListener("resize", calculate);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", calculate);
    };
  }, []);

  const handleDeleteConversation = async (conversationId) => {
    try {
      await deleteChatbotRoom({ accessToken, roomId: conversationId });

      toast.success("گفتگو با موفقیت حذف شد.");

      if (selectedConversation?.id === conversationId) {
        setSelectedConversation(null);
      }

      await loadConversations();
    } catch (error) {
      console.error("خطا در حذف گفتگو:", error);
      toast.error(error?.message || "خطا در حذف گفتگو");
      throw error;
    }
  };

  const handleRoomCreated = (roomData) => {
    setSelectedConversation(roomData);
    setWelcomeMessage(roomData.welcomeMessage || "");
    loadConversations();
  };
  console.log("conversations---> ", conversations)
  return (
    <SidebarProvider>

      <MobileSidebarToggle />

      <ClientSidebar
        username={username}
        name={name}
        conversations={conversations}
        onNewChat={handleNewChat}
        onConversationClick={handleConversationClick}
        activeConversationId={selectedConversation?.id}
        onDeleteConversation={handleDeleteConversation}
        isCustomerPaymentActive={isCustomerPaymentActive}
      />

      <div className="flex flex-col h-screen client-main-content !bg-gray-50 text-gray-900 dark:!bg-gray-950 dark:text-gray-100">
        <div className="flex-1 min-h-0 px-1 md:px-6 py-0 overflow-hidden">
          <ClientContentRouter
            isRoomLoading={isRoomLoading}
            isLoading={isLoading}
            error={error}
            loading={loading}
            conversations={conversations}
            headerTitle={headerTitle}
            selectedConversation={selectedConversation}
            handleConversationClick={handleConversationClick}
            handleDeleteConversation={handleDeleteConversation}
            selectedMenu={selectedMenu}
            username={username}
            userId={userId}
            accessToken={accessToken}
            name={name}
            isBotActive={isBotActive}
            toggleView={toggleView}
            isChatBotVisible={isChatBotVisible}
            isEdit={isEdit}
            isPaymentFeedback={isPaymentFeedback}
            isChangePassword={isChangePassword}
            isTransactionList={isTransactionList}
            welcomeMessage={welcomeMessage}
            onRoomCreated={handleRoomCreated}
          />
        </div>
      </div>
    </SidebarProvider>
  );
};

export default ClientChatBotContainer;